/**
 * Pure data model behind the review step. No React, no Backstage internals.
 *
 * Day-2 answers a different question from Day-1. Day-1 asks "is this what you
 * want to create?", so the whole form is worth reading back. Day-2 asks "is
 * this what you want to CHANGE?", and printing forty unchanged fields to hide
 * three changed ones is how a destructive edit gets waved through. So the Day-2
 * model emits changed leaves only, as Before | After.
 *
 * Schema information comes from `ReviewStepProps.steps[].mergedSchema`, which
 * Backstage has already resolved for the current form data. Labels are also
 * collected from every conditional branch of the raw `schema`, so a field that
 * only exists on the ORIGINAL side of a change still gets its human title
 * rather than its raw path.
 */

export interface ReviewStep {
  uiSchema: Record<string, any>;
  mergedSchema: Record<string, any>;
  schema: Record<string, any>;
}

export interface ReviewValue {
  display: string | string[];
  /** Array members present in After but not Before. */
  added?: string[];
  /** Array members present in Before but not After. */
  removed?: string[];
}

export interface ReviewRow {
  path: string;
  label: string;
  before: ReviewValue;
  after: ReviewValue;
}

export interface ReviewGroup {
  title: string;
  rows: ReviewRow[];
}

/**
 * Fields that exist to drive the wizard, not to describe the app: the Day-2
 * snapshot, the warning and cost display fields, and the old confirmation
 * object. Showing any of them as a "change" would be noise at best and, in
 * `_original`'s case, a wall of duplicated JSON.
 */
const INTERNAL_FIELDS = new Set([
  '_original',
  'day2_warnings',
  'day2_confirm',
  'cost_estimation',
]);

const ABSENT = '—';
const MASK = '******';

function humanize(key: string): string {
  const spaced = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Empty string, null, undefined and the empty array all mean "nothing here". */
function isAbsent(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  return Array.isArray(value) && value.length === 0;
}

interface FieldMeta {
  title?: string;
  /**
   * Every enum value this field can hold, mapped to its display name, UNIONED
   * across conditional branches. A field like `frontend` is declared once per
   * branch with a different list each time — SPA frameworks in one, SSR in the
   * other — and a change that moves between branches needs labels from both.
   * Keeping only the last branch read leaves the other side of the comparison
   * showing a raw id like `react`.
   */
  enumLabels: Map<string, string>;
  masked?: boolean;
}

function enumEntries(property: Record<string, any>): [unknown[], string[]] {
  const items = property.items as Record<string, any> | undefined;
  const values = property.enum ?? items?.enum ?? [];
  const names = property.enumNames ?? items?.enumNames ?? [];
  return [values, names];
}

/**
 * Collects property metadata from a schema and from every conditional branch
 * inside it. A `dependencies`/`oneOf`/`allOf` branch that the current form data
 * does not select still describes fields the ORIGINAL data may have had.
 */
function collectFieldMeta(
  schema: Record<string, any> | undefined,
  uiSchema: Record<string, any> | undefined,
  into: Map<string, FieldMeta>,
): void {
  if (!schema || typeof schema !== 'object') return;

  for (const [key, raw] of Object.entries(schema.properties ?? {})) {
    const property = raw as Record<string, any>;
    const ui = (uiSchema ?? {})[key] as Record<string, any> | undefined;
    const existing = into.get(key) ?? { enumLabels: new Map<string, string>() };
    const [values, names] = enumEntries(property);
    values.forEach((value, index) => {
      if (names[index] !== undefined) {
        existing.enumLabels.set(JSON.stringify(value), names[index]);
      }
    });

    into.set(key, {
      title: property.title ?? existing.title,
      enumLabels: existing.enumLabels,
      masked:
        existing.masked ||
        ui?.['ui:widget'] === 'password' ||
        ui?.['ui:field'] === 'Secret' ||
        Boolean(ui?.['ui:backstage.review']?.mask),
    });
  }

  for (const branch of [
    ...(Array.isArray(schema.allOf) ? schema.allOf : []),
    ...(Array.isArray(schema.oneOf) ? schema.oneOf : []),
    ...(Array.isArray(schema.anyOf) ? schema.anyOf : []),
    ...Object.values(schema.dependencies ?? {}).flatMap((dependency: any) =>
      Array.isArray(dependency?.oneOf) ? dependency.oneOf : [dependency],
    ),
  ]) {
    collectFieldMeta(branch as Record<string, any>, uiSchema, into);
  }
}

function labelForEnum(value: unknown, meta: FieldMeta | undefined): string {
  return meta?.enumLabels.get(JSON.stringify(value)) ?? String(value);
}

function toReviewValue(
  value: unknown,
  meta: FieldMeta | undefined,
  other: unknown,
  side: 'before' | 'after',
): ReviewValue {
  if (meta?.masked) return { display: isAbsent(value) ? ABSENT : MASK };
  if (isAbsent(value)) return { display: ABSENT };

  if (typeof value === 'boolean') return { display: value ? 'Yes' : 'No' };

  if (Array.isArray(value)) {
    const display = value.map(entry => labelForEnum(entry, meta));
    const otherList = Array.isArray(other)
      ? other.map(entry => labelForEnum(entry, meta))
      : [];
    const missing = display.filter(entry => !otherList.includes(entry));

    // Before highlights what is being taken away, After what is being added.
    return side === 'after'
      ? { display, added: missing }
      : { display, removed: missing };
  }

  if (isPlainObject(value)) return { display: JSON.stringify(value) };

  return { display: labelForEnum(value, meta) };
}

/** Two values are the same change-wise when both normalize to the same thing. */
function sameValue(a: unknown, b: unknown): boolean {
  if (isAbsent(a) && isAbsent(b)) return true;
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

interface Leaf {
  path: string;
  /** Path segments below the top-level property, for nested objects. */
  suffix: string[];
  before: unknown;
  after: unknown;
}

/**
 * Walks a top-level property down to the values worth comparing. A nested
 * object of per-environment settings becomes one leaf per setting, so a change
 * to one environment's API domain does not read as "the whole domains object
 * changed".
 */
function leavesOf(
  key: string,
  before: unknown,
  after: unknown,
  suffix: string[] = [],
): Leaf[] {
  const bothObjects =
    (isPlainObject(before) || before === undefined) &&
    (isPlainObject(after) || after === undefined) &&
    (isPlainObject(before) || isPlainObject(after));

  if (bothObjects) {
    const keys = [
      ...new Set([
        ...Object.keys(isPlainObject(before) ? before : {}),
        ...Object.keys(isPlainObject(after) ? after : {}),
      ]),
    ];
    return keys.flatMap(child =>
      leavesOf(
        key,
        isPlainObject(before) ? before[child] : undefined,
        isPlainObject(after) ? after[child] : undefined,
        [...suffix, child],
      ),
    );
  }

  return [
    { path: [key, ...suffix].join('.'), suffix, before, after },
  ];
}

export function buildDay2ReviewGroups(
  original: Record<string, unknown> | undefined,
  current: Record<string, unknown>,
  steps: ReviewStep[],
): ReviewGroup[] {
  const groups: ReviewGroup[] = [];
  const before = original ?? {};

  for (const step of steps) {
    const meta = new Map<string, FieldMeta>();
    collectFieldMeta(step.mergedSchema, step.uiSchema, meta);
    collectFieldMeta(step.schema, step.uiSchema, meta);

    const title = String(
      step.mergedSchema?.title ?? step.schema?.title ?? 'Changes',
    );
    const rows: ReviewRow[] = [];

    // From the collected metadata, not from the merged properties: a field the
    // ORIGINAL app had, whose conditional branch the current data no longer
    // selects, is exactly the kind of change this table exists to show.
    const keys = [...meta.keys()];

    for (const key of keys) {
      if (INTERNAL_FIELDS.has(key)) continue;
      const fieldMeta = meta.get(key);

      for (const leaf of leavesOf(key, before[key], current[key])) {
        if (sameValue(leaf.before, leaf.after)) continue;
        // A leaf that is absent on both sides is not a change, whatever shape
        // the surrounding objects happen to have.
        if (isAbsent(leaf.before) && isAbsent(leaf.after)) continue;

        rows.push({
          path: leaf.path,
          label: leaf.suffix.length
            ? [
                title,
                // Intermediate segments are keys the user chose — environment
                // names like `prod-eu` — and stay verbatim. Only the leaf is a
                // field name worth turning into prose.
                ...leaf.suffix.slice(0, -1),
                humanize(leaf.suffix[leaf.suffix.length - 1]),
              ].join(' · ')
            : fieldMeta?.title ?? humanize(key),
          before: toReviewValue(leaf.before, fieldMeta, leaf.after, 'before'),
          after: toReviewValue(leaf.after, fieldMeta, leaf.before, 'after'),
        });
      }
    }

    if (rows.length > 0) groups.push({ title, rows });
  }

  return groups;
}

/**
 * The Day-1 review, rebuilt from the public step schemas.
 *
 * This exists so the custom review component can serve every OTHER template
 * without importing Backstage's private implementation of the same thing. It
 * keeps the `ui:backstage.review` semantics those templates already rely on.
 */
export function buildStandardReviewData(
  formData: Record<string, unknown>,
  steps: ReviewStep[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const step of steps) {
    const meta = new Map<string, FieldMeta>();
    collectFieldMeta(step.mergedSchema, step.uiSchema, meta);
    collectFieldMeta(step.schema, step.uiSchema, meta);

    for (const key of Object.keys(step.mergedSchema?.properties ?? {})) {
      if (INTERNAL_FIELDS.has(key)) continue;

      const ui = (step.uiSchema ?? {})[key] ?? {};
      const review = ui['ui:backstage.review'] ?? {};
      if (review.show === false) continue;

      const value = formData[key];
      if (value === undefined) continue;

      const name = review.name ?? meta.get(key)?.title ?? key;

      if (review.mask) {
        result[name] = review.mask;
        continue;
      }
      if (meta.get(key)?.masked) {
        result[name] = MASK;
        continue;
      }

      if (isPlainObject(value) && review.explode !== false) {
        for (const [child, childValue] of Object.entries(value)) {
          result[`${name}.${child}`] = childValue;
        }
        continue;
      }

      if (Array.isArray(value)) {
        result[name] = value.map(entry => labelForEnum(entry, meta.get(key)));
      } else if (isPlainObject(value)) {
        // `explode: false` asked for the object itself, not a rendering of it.
        result[name] = value;
      } else {
        result[name] = labelForEnum(value, meta.get(key));
      }
    }
  }

  return result;
}
